import { colors } from '@kmns-internal/furikake'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import RemoveIcon from '@mui/icons-material/Remove'
import { Box, SvgIcon } from '@mui/material'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import CheckExclamation from 'src/assets/icons/check_exclamation.svg?react'
import type { ScheduledReportDailyStatus } from 'src/features/scheduledReportSummary/types'

type Props = {
  reportUuid?: string
  status: ScheduledReportDailyStatus
}

const containerSx = {
  alignItems: 'center',
  display: 'flex',
  height: '38px',
  justifyContent: 'center',
} as const

/**
 * 日毎の実施状況を表示するセル
 */
export const StatusCell = (props: Props) => {
  const { reportUuid, status } = props

  const wrapWithLink = (children: ReactNode) => {
    if (reportUuid) {
      return <Link to={`/reports/${reportUuid}`}>{children}</Link>
    } else {
      return children
    }
  }

  switch (status) {
    case 'implemented': {
      return wrapWithLink(
        <Box
          aria-label="実施済み"
          sx={{
            ...containerSx,
            color: colors.text.secondary,
            '&:hover': { bgcolor: colors.action.hover },
          }}
        >
          <CheckIcon />
        </Box>,
      )
    }
    case 'implementedAfterDeadline': {
      return wrapWithLink(
        <Box
          aria-label="期限切れで実施"
          sx={{
            ...containerSx,
            bgcolor: colors.warning.hover,
            color: colors.warning.main,
            '&:hover': { bgcolor: colors.warning.selected },
          }}
        >
          <SvgIcon component={CheckExclamation} inheritViewBox />
        </Box>,
      )
    }
    case 'notScheduled': {
      return (
        <Box
          aria-label="予定なし"
          sx={{ ...containerSx, color: colors.text.disabled }}
        >
          <RemoveIcon />
        </Box>
      )
    }
    case 'notImplementedYet': {
      return (
        <Box
          aria-label="未実施"
          sx={{
            ...containerSx,
            bgcolor: colors.error.hover,
            color: colors.error.main,
          }}
        >
          <CloseIcon />
        </Box>
      )
    }
    case 'notStarted': {
      return (
        <Box
          aria-label="開始前"
          sx={{ ...containerSx, color: colors.text.secondary }}
        >
          <Box sx={containerSx}>開始前</Box>
        </Box>
      )
    }
    case 'duringPeriod': {
      return (
        <Box aria-label="期間中" sx={containerSx}>
          <Box sx={containerSx}>期間中</Box>
        </Box>
      )
    }
    default: {
      return null
    }
  }
}
